import React, { useState } from 'react';
import Stack from 'react-bootstrap/Stack';
import { Form, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import type { Insight, InsightAction } from './types';

interface ActionOption {
  labelKey: string;
  value: string;
  isSelected?: boolean;
  isDisabled?: boolean;
}

interface InsightDetailsFeature {
  type: 'select' | 'text';
  order: number;
  label: string;
  options?: ActionOption[];
}

interface InsightItemFormProps {
  item: Insight;
  handleCloseInsight: (
    event: React.MouseEvent<HTMLButtonElement>,
    insightState: InsightAction,
  ) => Promise<void> | void;
  handleCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleInsightSubmit: (insightState: InsightAction) => Promise<void> | void;
  popout?: boolean;
}

const InsightItemForm: React.FC<InsightItemFormProps> = ({
  item,
  handleCancel,
  handleCloseInsight,
  handleInsightSubmit,
  popout = false,
}) => {
  const { selectedTenancy } = useSelector(
    (state: any) => state.clinicianProfile,
  );

  // Get dynamic field configuration
  const insightDetailsFields: InsightDetailsFeature[] =
    selectedTenancy?.features?.insightsConfig?.insightActions || [];
  // Helper functions for dynamic field configuration
  const getFieldConfig = (label: string) => {
    return insightDetailsFields.find((field: any) => field.label === label);
  };

  // Helper function to get options for a field
  const getFieldOptions = (label: string) => {
    const fieldConfig = getFieldConfig(label);
    return fieldConfig?.options || [];
  };

  const getInitialStateValue = (label: string) => {
    const options = getFieldOptions(label);
    const selectedOption = options.find((option: any) => option.isSelected);
    return selectedOption ? selectedOption.value : options?.[0]?.value || '';
  };

  const renderInsightDetailsFields = () => {
    if (!insightDetailsFields || insightDetailsFields.length === 0) {
      return null;
    }

    // Create a copy of the array before sorting to avoid read-only array error
    const sortedFields = [...insightDetailsFields].sort(
      (a: any, b: any) => a.order - b.order,
    );

    return sortedFields.map((fieldConfig: any, index: number) => {
      if (fieldConfig.type === 'select') {
        return renderSelectField(fieldConfig, index);
      } else if (fieldConfig.type === 'text') {
        return renderTextField(fieldConfig, index);
      }
      return null;
    });
  };

  // Render select field based on configuration
  const renderSelectField = (fieldConfig: any, index: number) => {
    const fieldId = `select-${index}`;
    let currentValue = '';
    let onChangeHandler:
      | ((ev: React.ChangeEvent<HTMLSelectElement>) => void)
      | null = null;

    // Map field labels to state and handlers
    switch (fieldConfig.label) {
      case 'Add new action':
        currentValue = actOptionsState;
        onChangeHandler = (ev) => insightField('action_status', ev);
        break;
      case 'Current mental illness':
        currentValue = aOptionsState;
        onChangeHandler = (ev) => insightField('current_mental_ilness', ev);
        break;
      case 'Subsequent support':
        currentValue = sOptionsState;
        onChangeHandler = (ev) => insightField('subsequent_support', ev);
        break;
      default:
        return null;
    }

    return (
      <div key={fieldId} className="flexVertWrap mb-4">
        <label
          className="txtLeft txtDropdownLabel"
          style={{ whiteSpace: 'normal' }}
        >
          {fieldConfig.label}
        </label>
        <Form.Select
          className={'insightSelect'}
          id={fieldId}
          value={currentValue}
          onChange={onChangeHandler}
        >
          {fieldConfig.options.map((option: any) => (
            <option
              key={option.labelKey}
              value={option.labelKey}
              disabled={option.isDisabled}
            >
              {option.value}
            </option>
          ))}
        </Form.Select>
      </div>
    );
  };

  // Render text field based on configuration
  const renderTextField = (fieldConfig: any, index: number) => {
    const fieldId = `text-${index}`;
    let currentValue = '';
    let onChangeHandler:
      | ((ev: React.ChangeEvent<HTMLTextAreaElement>) => void)
      | null = null;

    // Map field labels to state and handlers
    switch (fieldConfig.label) {
      case 'Reason for next steps':
        currentValue = actRState;
        onChangeHandler = (ev) => insightField('reason', ev);
        break;
      default:
        return null;
    }

    return (
      <div key={fieldId} className="flexVertWrap mb-4">
        <label
          className="txtLeft txtDropdownLabel"
          style={{ whiteSpace: 'normal' }}
        >
          {fieldConfig.label}
        </label>
        <Form.Control
          as="textarea"
          rows={1}
          id={fieldId}
          value={currentValue}
          onChange={onChangeHandler}
        />
      </div>
    );
  };

  const initialInsightAction: InsightAction = {
    insightId: item.id,
    insight_status: item.insight_status || '',
    action_status: '-Select-',
    conditional_answer: '',
    reason: '',
    current_mental_illness: 'Not assessed',
    subsequent_support: 'Same',
  };

  const [sOptionsState, setSOptions] = useState<string>(
    getInitialStateValue('Subsequent support:') || 'Same',
  );
  const [aOptionsState, setAOptions] = useState<string>(
    getInitialStateValue('Current mental illness:') || 'Not assessed',
  );
  const [actOptionsState, setActOptions] = useState<string>(
    getInitialStateValue('Add new action') || '-Select-',
  );
  const [actRState, setROptions] = useState<string>('');
  const [insightState, setInsightState] =
    useState<InsightAction>(initialInsightAction);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const insightField = (
    field: string,
    selectedOptions: React.ChangeEvent<
      HTMLSelectElement | HTMLTextAreaElement | HTMLInputElement
    >,
  ) => {
    const updatedInsightState = { ...insightState };

    switch (field) {
      case 'action_status':
        updatedInsightState.action_status =
          selectedOptions.target.value === 'Review'
            ? null
            : selectedOptions.target.value;
        setActOptions(selectedOptions.target.value);
        break;
      case 'current_mental_ilness':
        updatedInsightState.current_mental_illness =
          selectedOptions.target.value;
        setAOptions(selectedOptions.target.value);
        break;
      case 'subsequent_support':
        updatedInsightState.subsequent_support = selectedOptions.target.value;
        setSOptions(selectedOptions.target.value);
        break;
      case 'reason':
        updatedInsightState.reason = selectedOptions.target.value;
        setROptions(selectedOptions.target.value);
        break;
      default:
        break;
    }
    setInsightState(updatedInsightState);
  };

  const markInsightAsClosed = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (isSubmitting || isClosing) return;
    const updatedInsightState = {
      ...insightState,
      action_status: 'C',
      current_mental_illness: 'Not assessed',
      subsequent_support: 'Same',
    };
    setInsightState(updatedInsightState);
    setIsClosing(true);
    try {
      await handleCloseInsight(event, updatedInsightState);
    } finally {
      setIsClosing(false);
    }
  };

  const onResetForm = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isSubmitting || isClosing) return;
    handleCancel(event);

    const resetInsightAction = { ...initialInsightAction };
    resetInsightAction.action_status = getInitialStateValue('Add new action');
    setActOptions(getInitialStateValue('Add new action'));

    resetInsightAction.current_mental_illness = getInitialStateValue(
      'Current mental illness:',
    );
    setAOptions(getInitialStateValue('Current mental illness:'));

    resetInsightAction.subsequent_support = getInitialStateValue(
      'Subsequent support:',
    );
    setSOptions(getInitialStateValue('Subsequent support:'));

    resetInsightAction.reason = '';
    setROptions('');

    setInsightState(resetInsightAction);
  };

  const onSubmitForm: React.FormEventHandler<HTMLFormElement> = async (
    event,
  ) => {
    event.preventDefault();
    if (isSubmitting || isClosing) return;
    setIsSubmitting(true);
    try {
      await handleInsightSubmit(insightState);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!item) {
    return null;
  }

  return (
    <Form onSubmit={onSubmitForm}>
      <div id="txt3" className="flexVert divSelect">
        {renderInsightDetailsFields()}
      </div>

      {popout && (
        <button
          type="submit"
          className="btn btnPrimary btn-xs btn-long insight-popout-submit"
          disabled={isSubmitting || isClosing}
        >
          {isSubmitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Submitting
            </>
          ) : (
            'Submit'
          )}
        </button>
      )}

      {!popout && (
        <Stack direction="horizontal" gap={2}>
          {actOptionsState === '-Select-' && (
            <button
              type="button"
              className="btn btnSecondary btn-long btn-xs"
              onClick={markInsightAsClosed}
              disabled={isSubmitting || isClosing}
            >
              {isClosing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Closing
                </>
              ) : (
                'Mark insight as closed'
              )}
            </button>
          )}

          <button
            type="button"
            className="btn btnSecondary btn-long btn-xs ms-auto"
            onClick={onResetForm}
            disabled={isSubmitting || isClosing}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="btn btnPrimary btn-xs btn-long"
            disabled={isSubmitting || isClosing}
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving
              </>
            ) : (
              'Save'
            )}
          </button>
        </Stack>
      )}
    </Form>
  );
};

export default InsightItemForm;
